/**
 * PushManager - Web Push Notification Manager
 *
 * Manages push notification subscriptions using an iframe-based approach.
 * The iframe is hosted on push.hamla.io and handles:
 * - Service Worker registration
 * - Push subscription management
 * - Permission requests
 *
 * This approach allows push notifications without merchants needing
 * to host any files on their domain.
 */

import { Logger } from '../core/Logger';
import type { EventBus } from '../core/EventBus';

/**
 * Push permission states
 */
export type PushPermission = 'default' | 'granted' | 'denied';

/**
 * Push subscription status
 */
export interface PushStatus {
  supported: boolean;
  permission: PushPermission;
  subscribed: boolean;
  error?: string;
}

/**
 * PushManager configuration
 */
export interface PushManagerConfig {
  /** Visitor ID for subscription linking */
  visitorId: string;
  /** Business ID for subscription linking */
  businessId: string;
  /** Push frame URL (default: https://push.hamla.io) */
  pushFrameUrl?: string;
  /** Enable debug logging */
  debug?: boolean;
}

/**
 * Message types from push-frame iframe
 */
type IframeMessageType =
  | 'HAMLA_PUSH_FRAME_LOADED'
  | 'HAMLA_PUSH_READY'
  | 'HAMLA_PUSH_SUBSCRIBED'
  | 'HAMLA_PUSH_UNSUBSCRIBED'
  | 'HAMLA_PUSH_STATUS';

/**
 * Message from iframe
 */
interface IframeMessage {
  type: IframeMessageType;
  supported?: boolean;
  permission?: PushPermission;
  subscribed?: boolean;
  success?: boolean;
  error?: string;
  reason?: string;
}

/**
 * PushManager class
 */
export class PushManager {
  private logger: Logger;
  private config: Required<PushManagerConfig>;
  private eventBus: EventBus | null;

  private iframe: HTMLIFrameElement | null = null;
  private frameReady: boolean = false;
  private frameLoaded: boolean = false;
  private pendingCallbacks: Map<string, (data: IframeMessage) => void> = new Map();

  // Current state
  private _supported: boolean = false;
  private _permission: PushPermission = 'default';
  private _subscribed: boolean = false;

  constructor(config: PushManagerConfig, eventBus?: EventBus | null) {
    this.config = {
      pushFrameUrl: 'https://push.hamla.io',
      debug: false,
      ...config,
    };
    this.logger = new Logger('[PushManager]');
    this.eventBus = eventBus || null;

    // Listen for messages from iframe
    if (typeof window !== 'undefined') {
      window.addEventListener('message', this.handleMessage.bind(this));
    }
  }

  /**
   * Initialize push manager (creates iframe and checks support)
   */
  async init(): Promise<PushStatus> {
    this.logger.debug('Initializing PushManager');

    // Check basic browser support
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      this.logger.warn('Push notifications not supported in this environment');
      return {
        supported: false,
        permission: 'default',
        subscribed: false,
        error: 'Push not supported',
      };
    }

    // Create iframe if not exists
    if (!this.iframe) {
      await this.createIframe();
    }

    // Wait for frame to be ready
    await this.waitForFrameReady();

    // Send init message to iframe
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        this.pendingCallbacks.delete('init');
        resolve({
          supported: false,
          permission: 'default',
          subscribed: false,
          error: 'Init timeout',
        });
      }, 10000);

      this.pendingCallbacks.set('init', (data) => {
        clearTimeout(timeout);
        this._supported = data.supported ?? false;
        this._permission = data.permission ?? 'default';
        this._subscribed = data.subscribed ?? false;

        resolve({
          supported: this._supported,
          permission: this._permission,
          subscribed: this._subscribed,
          error: data.reason,
        });
      });

      this.postToFrame('HAMLA_PUSH_INIT', {
        visitorId: this.config.visitorId,
        businessId: this.config.businessId,
      });
    });
  }

  /**
   * Request push permission and subscribe
   */
  async subscribe(): Promise<PushStatus> {
    if (!this._supported) {
      return {
        supported: false,
        permission: this._permission,
        subscribed: false,
        error: 'Push not supported',
      };
    }

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        this.pendingCallbacks.delete('subscribe');
        resolve({
          supported: true,
          permission: this._permission,
          subscribed: false,
          error: 'Subscribe timeout',
        });
      }, 30000); // Longer timeout for user interaction

      this.pendingCallbacks.set('subscribe', (data) => {
        clearTimeout(timeout);

        if (data.success) {
          this._subscribed = true;
          this._permission = 'granted';

          // Emit event
          this.eventBus?.emit('push:subscribed' as any, {
            timestamp: Date.now(),
            visitorId: this.config.visitorId,
          });
        } else if (data.permission === 'denied') {
          this._permission = 'denied';
        }

        resolve({
          supported: true,
          permission: this._permission,
          subscribed: this._subscribed,
          error: data.error,
        });
      });

      this.postToFrame('HAMLA_PUSH_SUBSCRIBE', {
        visitorId: this.config.visitorId,
        businessId: this.config.businessId,
      });
    });
  }

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribe(): Promise<PushStatus> {
    if (!this._subscribed) {
      return {
        supported: this._supported,
        permission: this._permission,
        subscribed: false,
      };
    }

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        this.pendingCallbacks.delete('unsubscribe');
        resolve({
          supported: this._supported,
          permission: this._permission,
          subscribed: this._subscribed,
          error: 'Unsubscribe timeout',
        });
      }, 10000);

      this.pendingCallbacks.set('unsubscribe', (data) => {
        clearTimeout(timeout);

        if (data.success) {
          this._subscribed = false;

          // Emit event
          this.eventBus?.emit('push:unsubscribed' as any, {
            timestamp: Date.now(),
            visitorId: this.config.visitorId,
          });
        }

        resolve({
          supported: this._supported,
          permission: this._permission,
          subscribed: this._subscribed,
          error: data.error,
        });
      });

      this.postToFrame('HAMLA_PUSH_UNSUBSCRIBE', {
        visitorId: this.config.visitorId,
        businessId: this.config.businessId,
      });
    });
  }

  /**
   * Get current push status
   */
  async getStatus(): Promise<PushStatus> {
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        this.pendingCallbacks.delete('status');
        resolve({
          supported: this._supported,
          permission: this._permission,
          subscribed: this._subscribed,
        });
      }, 5000);

      this.pendingCallbacks.set('status', (data) => {
        clearTimeout(timeout);

        this._supported = data.supported ?? this._supported;
        this._permission = data.permission ?? this._permission;
        this._subscribed = data.subscribed ?? this._subscribed;

        resolve({
          supported: this._supported,
          permission: this._permission,
          subscribed: this._subscribed,
          error: data.error,
        });
      });

      this.postToFrame('HAMLA_PUSH_STATUS', {});
    });
  }

  /**
   * Check if push is supported
   */
  get isSupported(): boolean {
    return this._supported;
  }

  /**
   * Check if user is subscribed
   */
  get isSubscribed(): boolean {
    return this._subscribed;
  }

  /**
   * Get current permission state
   */
  get permission(): PushPermission {
    return this._permission;
  }

  /**
   * Destroy the push manager
   */
  destroy(): void {
    if (this.iframe && this.iframe.parentNode) {
      this.iframe.parentNode.removeChild(this.iframe);
      this.iframe = null;
    }
    this.frameReady = false;
    this.frameLoaded = false;
    this.pendingCallbacks.clear();

    if (typeof window !== 'undefined') {
      window.removeEventListener('message', this.handleMessage.bind(this));
    }
  }

  // ============================================================================
  // Private Methods
  // ============================================================================

  /**
   * Create the hidden iframe
   */
  private async createIframe(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (typeof document === 'undefined') {
        reject(new Error('Document not available'));
        return;
      }

      this.iframe = document.createElement('iframe');
      this.iframe.src = `${this.config.pushFrameUrl}/frame.html`;
      this.iframe.id = 'hamla-push-frame';
      this.iframe.style.cssText = 'display:none;width:0;height:0;border:0;position:absolute;';
      this.iframe.setAttribute('aria-hidden', 'true');

      this.iframe.onload = () => {
        this.frameLoaded = true;
        this.logger.debug('Push frame loaded');
        resolve();
      };

      this.iframe.onerror = () => {
        reject(new Error('Failed to load push frame'));
      };

      document.body.appendChild(this.iframe);
    });
  }

  /**
   * Wait for frame to be ready (SW registered)
   */
  private async waitForFrameReady(): Promise<void> {
    if (this.frameReady) return;

    // Wait for frame to load first
    if (!this.frameLoaded) {
      await new Promise<void>((resolve) => {
        const checkLoaded = setInterval(() => {
          if (this.frameLoaded) {
            clearInterval(checkLoaded);
            resolve();
          }
        }, 100);

        // Timeout after 10 seconds
        setTimeout(() => {
          clearInterval(checkLoaded);
          resolve();
        }, 10000);
      });
    }
  }

  /**
   * Handle messages from iframe
   */
  private handleMessage(event: MessageEvent): void {
    // Validate origin
    const expectedOrigin = new URL(this.config.pushFrameUrl).origin;
    if (event.origin !== expectedOrigin) {
      return;
    }

    const data = event.data as IframeMessage;
    if (!data || !data.type) return;

    this.logger.debug('Received message from push frame', data);

    switch (data.type) {
      case 'HAMLA_PUSH_FRAME_LOADED':
        this.frameLoaded = true;
        break;

      case 'HAMLA_PUSH_READY':
        this.frameReady = true;
        this._supported = data.supported ?? false;
        this._permission = data.permission ?? 'default';
        this._subscribed = data.subscribed ?? false;
        this.pendingCallbacks.get('init')?.(data);
        this.pendingCallbacks.delete('init');
        break;

      case 'HAMLA_PUSH_SUBSCRIBED':
        this.pendingCallbacks.get('subscribe')?.(data);
        this.pendingCallbacks.delete('subscribe');
        break;

      case 'HAMLA_PUSH_UNSUBSCRIBED':
        this.pendingCallbacks.get('unsubscribe')?.(data);
        this.pendingCallbacks.delete('unsubscribe');
        break;

      case 'HAMLA_PUSH_STATUS':
        this.pendingCallbacks.get('status')?.(data);
        this.pendingCallbacks.delete('status');
        break;
    }
  }

  /**
   * Post message to iframe
   */
  private postToFrame(type: string, payload: Record<string, unknown>): void {
    if (!this.iframe?.contentWindow) {
      this.logger.warn('Cannot post to frame: iframe not ready');
      return;
    }

    this.iframe.contentWindow.postMessage(
      { type, payload },
      this.config.pushFrameUrl
    );
  }
}
